import { QueryObserverBaseResult } from "@tanstack/react-query"
import { some, get, chain, isString } from "lodash"

export const OK = "ok"
export const LOADING = "loading"
export const ERROR = "error"
export const UNKNOWN = "unknown"

export type DataStatus = typeof OK | typeof LOADING | typeof ERROR | typeof UNKNOWN

export const DATA_STATUSES: DataStatus[] = [OK, LOADING, ERROR, UNKNOWN]

type OkResourceResult<R> = [R | null, typeof OK]
type ErrorResourceResult = [null, typeof ERROR]
type LoadingResourceResult = [null, typeof LOADING]
type UnknownResourceResult = [null, typeof UNKNOWN]

export type ResourceResult<R> = OkResourceResult<R> | ErrorResourceResult | LoadingResourceResult | UnknownResourceResult

export const isLoading = (status: DataStatus): boolean => status === LOADING || status === UNKNOWN
export const isError = (status: DataStatus): boolean => status === ERROR

export function combineStatuses(statuses: DataStatus[]) {
  if (some(statuses, isError)) {
    return ERROR
  }
  if (some(statuses, isLoading)) {
    return LOADING
  }
  return OK
}

export function convertTanStackStatus(result: QueryObserverBaseResult | DataStatus | undefined): DataStatus {
  // We support null queries, so current could be undefined and should be ignored
  if (!result) {
    return OK
  }

  // If already a data status, return it as is
  if (isString(result)) {
    return result
  }

  // If tanstack query result, return appropriate type
  if (result.status === "error") {
    return ERROR
  }

  if (result.isFetching || result.status === "pending") {
    return LOADING
  }

  return OK
}

export type TanStackStatusInput = QueryObserverBaseResult | DataStatus | undefined

export function combineTanStackStatuses(
  results: TanStackStatusInput | TanStackStatusInput[],
): DataStatus {
  const list = Array.isArray(results) ? results : [results]
  const dataStatuses = list.map(convertTanStackStatus)

  // Use existing logic for combining data statuses
  return combineStatuses(dataStatuses)
}

export function combineResourcesStatuses(resources: string | string[], statuses: Record<string, DataStatus>): DataStatus {
  if (!statuses) {
    return OK
  }

  if (!Array.isArray(resources)) {
    resources = [resources]
  }

  return chain(resources)
    // Make sure that if the resource doesn't have a status yet, it will still be considered loading
    .map((resource) => get(statuses, resource, UNKNOWN))
    .thru(combineStatuses)
    .value()
}

export function areResourcesLoading(resources: string | string[], statuses: Record<string, DataStatus>): boolean {
  return combineResourcesStatuses(resources, statuses) === LOADING
}

export function hasResourcesLoadingError(resources: string | string[], statuses: Record<string, DataStatus>): boolean {
  return combineResourcesStatuses(resources, statuses) === ERROR
}
